import React, { useCallback, useState } from "react";
import classNames from "classnames";

import { ReactComponent as ArrowUpRightSVG } from "App/common/icons/arrow-up-right.svg";
import { ReactComponent as SendPlaneSVG } from "App/common/icons-v2/send-plane-line.svg";
import { Ad } from "./AdModel";
import { useAdsContext } from "./AdsContext";
import { HighlightedUrlText } from "./HighlightedUrlText";
import {
  AdTrackerParams,
  trackAdClick,
  useAdImpressionTracker,
} from "./useAdImpressionTracker";
import "./CarouselAdCard.scss";

export type CarouselAdCardProps = {
  ad: Ad;
  /**
   * Optional override for the click handler. Defaults to opening
   * `ad.linkUrl` in a new tab. Tests use this seam.
   */
  onClick?: (ad: Ad) => void;
  /**
   * Optional override for the "Sponsored" label text.
   */
  sponsoredLabel?: string;
  /**
   * Analytics injection seam. When provided, used in place of the
   * default `highnote.trackAdEvent` wrapper. Tests use this seam.
   */
  track?: AdTrackerParams["track"];
  /**
   * Optional override for the `spaceId` threaded into analytics. When
   * omitted (the usual case), the value is read from `AdsContext`. Kept
   * as a prop so callers outside the provider (e.g. Storybook, tests)
   * can still fire events with a deterministic spaceId.
   */
  spaceId?: string | null;
};

const DEFAULT_SPONSORED_LABEL = "Sponsored";
const SURFACE = "carousel" as const;

/**
 * Opens an ad link in a new tab with `noopener`/`noreferrer` semantics,
 * matching the Flutter behavior of launching an external URL without
 * leaking referrer info. Uses `window.open` rather than navigating the
 * current tab so the app never unmounts mid-session.
 */
const openAdLink = (ad: Ad) => {
  if (!ad.linkUrl) return;
  // `noopener,noreferrer` prevents the new tab from gaining a reference to
  // our window via `window.opener`, which is both a security best-practice
  // and required for privacy-preserving outbound links.
  window.open(ad.linkUrl, "_blank", "noopener,noreferrer");
};

/**
 * Deterministic misc-id picker that mirrors the `UserAvatar` fallback
 * colouring so a sponsored ad's initials tile uses one of the three
 * brand palettes the real `ConnectedUserAvatar` would have applied to a
 * user. We seed off `ad.id` so the colour is stable per ad across
 * renders (matches mobile's `HighnoteCircleAvatar(seed: adId)`).
 */
const pickMiscId = (seed: string): number => {
  if (!seed) return 1;
  const n = seed.toLowerCase().charAt(0);
  return n < "x" ? (n < "l" ? 1 : 2) : 3;
};

/**
 * Generates the initials shown in the avatar fallback. Mobile's
 * `HighnoteCircleAvatar` uses up to two characters from the display
 * name; we mirror that so the fallback tile reads the same as mobile.
 */
const getInitials = (name: string | null | undefined): string => {
  if (!name) return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return trimmed.slice(0, Math.min(2, trimmed.length)).toUpperCase();
};

/**
 * Advertiser avatar tile used inside the sponsored "comment". Mirrors
 * the structure of `ConnectedUserAvatar` (same `.highnote-user-avatar`
 * wrapper + `data-size` + `data-misc-id` attributes) so existing
 * carousel/comment CSS picks it up without modification. The image
 * source prefers `advertiserAvatarUrl`, falls back to `imageUrl`, and
 * finally renders an initials tile when both are missing or broken —
 * matching the Flutter `HighnoteCircleAvatar` behaviour verbatim.
 */
const AdvertiserAvatar = ({ ad }: { ad: Ad }) => {
  const primary = ad.advertiserAvatarUrl || null;
  const secondary = ad.imageUrl || null;
  // Track which source URL we are currently displaying. Start with the
  // primary; on error we fall through to secondary, then to initials.
  const [stage, setStage] = useState<"primary" | "secondary" | "initials">(
    primary ? "primary" : secondary ? "secondary" : "initials",
  );

  const handleError = useCallback(() => {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug(
        "[Ad] advertiser avatar failed to load",
        ad.id,
        stage === "primary" ? primary : secondary,
      );
    }
    if (stage === "primary" && secondary) {
      setStage("secondary");
    } else {
      setStage("initials");
    }
  }, [ad.id, stage, primary, secondary]);

  const src =
    stage === "primary" ? primary : stage === "secondary" ? secondary : null;
  const miscId = pickMiscId(ad.id || ad.advertiserName || "");
  const initials = getInitials(ad.advertiserName);

  return (
    <div
      className="highnote-user-avatar"
      data-size="medium"
      data-misc-id={miscId}
      data-theme="dark"
      data-ad-avatar="true"
    >
      {src ? (
        <img src={src} alt="" onError={handleError} />
      ) : (
        <div className="fallback" data-use-logo="false">
          {initials}
        </div>
      )}
    </div>
  );
};

/**
 * Sponsored ad card — ported from the Flutter
 * `carousel_comment_card.dart` on `main`, which maps advertiser data
 * into a regular `CommentCard` widget. A sponsored ad on mobile looks
 * exactly like a comment (avatar + name + subtitle + body), with ad
 * data in place of user data. This web port mirrors that: it reuses
 * the same DOM + CSS classes as `CommentCardPreview` (`.highnote-
 * comment-card-preview` → `.comment-wrapper` → `.meta-wrapper` /
 * `.highnote-comment-meta` / `.highnote-comment-content`) so the
 * carousel depth-of-field transforms and themed surface colours apply
 * identically to sponsored and non-sponsored cards alike.
 *
 * Field mapping (matches `CarouselCommentCard` on main):
 *   - Avatar ← `advertiserAvatarUrl` ?? `imageUrl` ?? initials
 *   - Name  ← `advertiserName`
 *   - Subtitle ← "Sponsored" (mirrors mobile `subtitleOverride`)
 *   - Body  ← `ad.body ?? ad.text`
 *
 * Explicitly NOT rendered: reply button, reply-count stats, reaction
 * badge, resolve/edit/delete menu, ad image block, CTA button. The
 * whole card is a single tap target; clicking opens `ad.linkUrl` in a
 * new tab and fires a `carousel`/`click` analytics event.
 */
const SponsoredCard = ({
  ad,
  onClick,
  sponsoredLabel,
  track,
  spaceId,
}: CarouselAdCardProps) => {
  const ctxSpaceId = useAdsContext().spaceId;
  const effectiveSpaceId = spaceId !== undefined ? spaceId : ctxSpaceId;
  const nodeRef = useAdImpressionTracker({
    ad,
    spaceId: effectiveSpaceId,
    surface: SURFACE,
    track,
  });

  const handle = useCallback(() => {
    trackAdClick(ad, effectiveSpaceId, SURFACE, track);
    if (onClick) {
      onClick(ad);
      return;
    }
    openAdLink(ad);
  }, [ad, onClick, effectiveSpaceId, track]);

  // Mobile falls back to `text` for the body copy. We prefer the
  // server-supplied `body` when present so newer ads that split fields
  // still render correctly, and fall back to `text` for legacy ads.
  const body = ad.body || ad.text || "";
  const label = sponsoredLabel ?? DEFAULT_SPONSORED_LABEL;

  return (
    <button
      type="button"
      ref={(el) => {
        nodeRef.current = el;
      }}
      className={classNames(
        "highnote-comment-card-preview",
        "highnote-ad-card--sponsored",
      )}
      onClick={handle}
      data-testid="ad-card"
      data-ad-type={ad.adType}
      data-ad-id={ad.id}
      data-sponsored-ad="true"
    >
      <div className="comment-wrapper">
        <div className="meta-wrapper">
          <div className="highnote-comment-meta" data-sponsored-ad-meta="true">
            <AdvertiserAvatar ad={ad} />
            <div className="comment-meta-content">
              <div className="user-info-row">
                <div className="user-name">
                  <span className="highnote-user-name">
                    {ad.advertiserName}
                  </span>
                </div>
              </div>
              <div className="created-at highnote-ad-card__sponsored-label">
                <ArrowUpRightSVG
                  className="highnote-ad-card__sponsored-icon"
                  aria-hidden
                  focusable={false}
                />
                <span>{label}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="scrollable">
          <div className="root-comment">
            <div
              className="highnote-comment-content"
              data-sponsored-ad-body="true"
            >
              <div className="comment-data">
                <div className="text-block highnote-ad-card__body-text">
                  <HighlightedUrlText
                    text={body}
                    maxLines={4}
                    onUrlTap={() => {
                      // Fire the same ad-click tracking the whole-card
                      // tap would fire. The anchor's own `target="_blank"`
                      // will open the URL; we only track here.
                      trackAdClick(ad, effectiveSpaceId, SURFACE, track);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

/**
 * Brand-partner ad card — ported verbatim from the Flutter
 * `carousel_brand_partner_card.dart` on `main`. The layout is a single
 * 1.75:1 aspect-ratio rounded tile with the ad image as the full-bleed
 * background and a translucent black CTA chip positioned at the bottom.
 * The chip contains the ad copy on the left and a send-plane icon on
 * the right.
 *
 * Explicitly NOT rendered: advertiser name, title headline, body
 * paragraph outside the chip, or any label above/below the image. The
 * whole card is a single tap target. The chip text resolves from
 * `ctaText` → `title` → `body` → legacy `text` so the chip is always
 * labelled when any copy is available.
 */
const BRAND_PARTNER_DEFAULT_CHIP = "Learn more";

const BrandPartnerCard = ({
  ad,
  onClick,
  track,
  spaceId,
}: CarouselAdCardProps) => {
  const ctxSpaceId = useAdsContext().spaceId;
  const effectiveSpaceId = spaceId !== undefined ? spaceId : ctxSpaceId;
  const nodeRef = useAdImpressionTracker({
    ad,
    spaceId: effectiveSpaceId,
    surface: SURFACE,
    track,
  });

  const handle = useCallback(() => {
    trackAdClick(ad, effectiveSpaceId, SURFACE, track);
    if (onClick) {
      onClick(ad);
      return;
    }
    openAdLink(ad);
  }, [ad, onClick, effectiveSpaceId, track]);

  // Chip text mirrors the mobile `CarouselBrandPartnerCard`, which
  // renders a single `text` line inside the chip. On web we widen the
  // fallback chain so any of the newer per-field strings can populate
  // the chip when `text` is empty — preserving mobile's intent (always
  // have a labeled tap target) without introducing a fallback surface
  // that doesn't exist on mobile.
  const chipLabel =
    (ad.ctaText && ad.ctaText.length > 0 ? ad.ctaText : null) ??
    ad.title ??
    ad.body ??
    (ad.text && ad.text.length > 0 ? ad.text : null) ??
    BRAND_PARTNER_DEFAULT_CHIP;

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug(
          "[Ad] brandPartner image failed to load",
          ad.id,
          e.currentTarget.src,
        );
      }
    },
    [ad.id],
  );

  return (
    <div
      ref={(el) => {
        nodeRef.current = el;
      }}
      className={classNames(
        "highnote-comment-card-preview",
        "highnote-ad-card",
        "highnote-ad-card--brand-partner",
      )}
      role="button"
      tabIndex={0}
      onClick={handle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handle();
        }
      }}
      data-testid="ad-card"
      data-ad-type={ad.adType}
      data-ad-id={ad.id}
    >
      {ad.imageUrl ? (
        <img
          className="highnote-ad-card__image"
          src={ad.imageUrl}
          alt=""
          decoding="async"
          onError={handleImageError}
        />
      ) : (
        <div
          className="highnote-ad-card__image highnote-ad-card__image--placeholder"
          aria-hidden
        />
      )}
      <div className="highnote-ad-card__chip">
        <span className="highnote-ad-card__chip-text">{chipLabel}</span>
        <SendPlaneSVG
          className="highnote-ad-card__chip-icon"
          aria-hidden
          focusable={false}
        />
      </div>
    </div>
  );
};

/**
 * Normalises the raw `adType` string so the dispatcher matches regardless
 * of casing and common separator variants. We've seen Firestore docs in
 * the wild with `"brandpartner"`, `"brand_partner"`, and `"brand-partner"`
 * in addition to the canonical `"brandPartner"` the serializer emits.
 * Treat them all as the same variant rather than silently falling back
 * to the sponsored layout.
 */
const isBrandPartnerAdType = (raw: string | null | undefined): boolean => {
  if (!raw) return false;
  return raw.toLowerCase().replace(/[_\-\s]/g, "") === "brandpartner";
};

/**
 * Dispatches to the correct variant based on `ad.adType`. Unknown types
 * fall back to the sponsored layout so we never render nothing.
 */
export const CarouselAdCard = (props: CarouselAdCardProps) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[Ad]", props.ad.id, props.ad.adType);
  }
  if (isBrandPartnerAdType(props.ad.adType)) {
    return <BrandPartnerCard {...props} />;
  }
  return <SponsoredCard {...props} />;
};
